import { problemIds, horizon } from './entitySchema';
import { scoreEvidenceForProblem, VALIDATION_THRESHOLD } from './validationScore';

export const entityPath = (type, id) => `/brain?type=${encodeURIComponent(type)}&id=${encodeURIComponent(id)}`;
export function initiativeReadiness(item, data) {
  const problems = data.problems.filter(p => problemIds(item).includes(String(p.id)));
  const hypotheses = data.hypotheses.filter(h => h.roadmap_item_id === item.id);
  const checks = [
    ['Validated problem', problems.some(p => ['validated', 'in_roadmap', 'solved'].includes(p.status))],
    ['Supporting evidence', problems.some(p => scoreEvidenceForProblem(data.evidence, p.id).score >= VALIDATION_THRESHOLD)],
    ['Success metric', Boolean(item.success_metric?.trim())],
    ['Objective', data.objectives.some(o => o.id === item.okr_id)],
    ['Owner', Boolean(item.owner?.trim())],
    ['Hypothesis', hypotheses.length > 0 || Boolean(item.hypothesis?.trim())],
  ];
  return { checks, score: Math.round(checks.filter(([, ok]) => ok).length / checks.length * 100) };
}

// PM Coach: ask the question each gap implies rather than blocking the PM.
const COACHING = {
  'Validated problem': 'Which customer problem does this solve, and how do you know it is real?',
  'Supporting evidence': 'What is the strongest evidence this matters, and what would contradict it?',
  'Success metric': 'What metric determines success, and what target would make it worth doing?',
  'Objective': 'Which objective does this move, and what would you stop doing to fund it?',
  'Owner': 'Who is accountable for the outcome, not only the delivery?',
  'Hypothesis': 'What is the falsifiable hypothesis, and what is the smallest experiment that tests it?',
};
export function coachingQuestions(readiness) {
  const gaps = readiness.checks.filter(([, ok]) => !ok).map(([label]) => COACHING[label]).filter(Boolean);
  return [...gaps, 'What result would make you stop or change direction?'].slice(0, 3);
}
export const challengePrompt = item => `Challenge the initiative "${item.title}" before it moves forward. Using its linked problem, evidence, hypothesis, metric, and objective, identify the weakest assumptions, missing evidence, and risks. Ask the questions a strong product leader would ask.`;

export function buildBrief(data, now = new Date()) {
  const insights = [];
  const today = now.toISOString().slice(0, 10);
  const add = (id, title, detail, type, entity, action, priority = 2) => insights.push({ id, title, detail, href: entityPath(type, entity.id), action, priority, source: 'Calculated from your records' });
  for (const item of data.initiatives.filter(i => horizon(i.time_horizon) === 'Now' && i.status !== 'done')) {
    const readiness = initiativeReadiness(item, data);
    const missing = readiness.checks.filter(([, ok]) => !ok).map(([label]) => label.toLowerCase());
    if (missing.length) add(`readiness:${item.id}`, item.title, `Missing: ${missing.join(', ')}. Readiness ${readiness.score}%.`, 'initiatives', item, 'Review initiative', 1);
  }
  for (const problem of data.problems) {
    const score = scoreEvidenceForProblem(data.evidence, problem.id);
    const linked = data.initiatives.some(i => problemIds(i).includes(String(problem.id)));
    if (!linked && (problem.status === 'validated' || score.score >= VALIDATION_THRESHOLD)) add(`validated:${problem.id}`, problem.title, `Evidence score ${score.score}/100. No initiative is linked.`, 'problems', problem, 'Review problem');
    if (score.lastEvidenceDays > 60) add(`stale:${problem.id}`, problem.title, `Latest evidence is ${score.lastEvidenceDays} days old.`, 'problems', problem, 'Refresh evidence', 3);
  }
  const nowItems = data.initiatives.filter(i => horizon(i.time_horizon) === 'Now' && i.status !== 'done')
    .map(i => ({ item: i, score: Math.max(0, ...problemIds(i).map(id => scoreEvidenceForProblem(data.evidence, id).score)) }));
  for (const problem of data.problems.filter(p => !['solved', 'archived'].includes(p.status) && !data.initiatives.some(i => problemIds(i).includes(String(p.id))))) {
    const score = scoreEvidenceForProblem(data.evidence, problem.id).score;
    const weaker = nowItems.filter(n => score - n.score >= 15);
    if (score >= VALIDATION_THRESHOLD && weaker.length) insights.push({
      id: `stronger:${problem.id}`, title: problem.title, priority: 1, source: 'Calculated from your records', action: 'Compare opportunities',
      detail: `Evidence score ${score}/100 is stronger than ${weaker.length} initiative${weaker.length === 1 ? '' : 's'} in Now (${weaker.map(n => n.item.title).join(', ')}).`,
      href: `/priorities?compare=${[`problems:${problem.id}`, ...weaker.slice(0, 3).map(n => `initiatives:${n.item.id}`)].map(encodeURIComponent).join(',')}`,
    });
  }
  for (const decision of data.decisions) {
    if (decision.review_date && decision.review_date <= today && decision.status !== 'reviewed') add(`decision:${decision.id}`, decision.title, `Decision review due ${decision.review_date}. Compare expected and actual outcomes.`, 'decisions', decision, 'Review decision', 1);
  }
  for (const experiment of data.experiments) {
    if (experiment.end_date && experiment.end_date <= today && experiment.status !== 'completed') add(`experiment:${experiment.id}`, experiment.title, 'The experiment has reached its review date.', 'experiments', experiment, 'Record result');
  }
  for (const issue of data.issues.filter(i => Number(i.tier) === 1 && i.status !== 'resolved')) add(`issue:${issue.id}`, issue.title, issue.description || 'Critical issue needs review.', 'issues', issue, 'Review issue', 0);
  // A comparison prompt supersedes the plain "no initiative" prompt for the same problem.
  const compared = new Set(insights.filter(i => i.id.startsWith('stronger:')).map(i => i.id.slice(9)));
  return insights.filter(i => !(i.id.startsWith('validated:') && compared.has(i.id.slice(10))))
    .sort((a, b) => a.priority - b.priority || a.id.localeCompare(b.id));
}

export function buildGraph(data) {
  const nodes = Object.entries(data).filter(([type]) => !['imports','insights','time'].includes(type)).flatMap(([type, records]) => Array.isArray(records) ? records.map(record => ({ ...record, type, label: record.title || record.statement || record.content?.slice(0, 90) || record.id })) : []);
  const keys = new Set(nodes.map(n => `${n.type}:${n.id}`));
  const edges = [];
  const link = (fromType, from, toType, to, relation) => {
    if (keys.has(`${fromType}:${from}`) && keys.has(`${toType}:${to}`)) edges.push({ fromType, from, toType, to, relation });
  };
  for (const i of data.initiatives) {
    problemIds(i).forEach(id => link('initiatives', i.id, 'problems', id, 'addresses'));
    link('initiatives', i.id, 'objectives', i.okr_id, 'contributes to');
  }
  for (const e of data.evidence) link('evidence', e.id, 'problems', e.problem_card_id, e.relationship || 'supports');
  for (const h of data.hypotheses) {
    link('hypotheses', h.id, 'initiatives', h.roadmap_item_id, 'tests');
    link('hypotheses', h.id, 'problems', h.problem_card_id, 'investigates');
  }
  for (const d of data.decisions) {
    link('decisions', d.id, 'initiatives', d.roadmap_item_id, 'affects');
    link('decisions', d.id, 'problems', d.problem_card_id, 'addresses');
    link('decisions', d.id, 'objectives', d.okr_id, 'supports');
    (d.evidence_ids || []).forEach(id => link('decisions', d.id, 'evidence', id, 'based on'));
  }
  for (const e of data.experiments) link('experiments', e.id, 'hypotheses', e.hypothesis_id, 'tests');
  for (const r of data.results) link('results', r.id, 'experiments', r.experiment_id, 'measures');
  for (const l of data.learnings) {
    link('learnings', l.id, 'results', l.result_id, 'concludes');
    link('learnings', l.id, 'decisions', l.decision_id, 'reviews');
  }
  return { nodes, edges };
}

export function matchProblems(text, problems) {
  const words = new Set((text.toLowerCase().match(/[a-z0-9]{3,}/g) || []).filter(w => !['the', 'and', 'that', 'with', 'this', 'from', 'have', 'they', 'customer'].includes(w)));
  return problems.map(p => {
    const tokens = new Set(`${p.title} ${p.description || ''}`.toLowerCase().match(/[a-z0-9]{3,}/g) || []);
    const overlap = [...words].filter(w => tokens.has(w)).length;
    return { problem: p, overlap };
  }).filter(m => m.overlap > 0).sort((a, b) => b.overlap - a.overlap).slice(0, 3);
}
